import { useCallback, useSyncExternalStore } from "react";
import { useUserDataQuery } from "@/services/graphql/reactQuery";

export const ACTIVE_PORTFOLIO_STORAGE_KEY = "activePortfolioId";
// The JWT `sub` of the user the override belongs to. sessionStorage is tab-scoped but
// not user-scoped: if a different user authenticates in the same tab without opening a
// fresh one, the prior user's override would otherwise linger. We stamp the owner and
// discard the override on boot when the current principal no longer matches.
export const ACTIVE_PORTFOLIO_OWNER_KEY = "activePortfolioOwner";
// sessionStorage is scoped to a single tab, so the cross-tab `storage` event never
// fires for it. We dispatch our own event to keep components in the same tab in sync.
export const ACTIVE_INSTITUTION_STORAGE_KEY = "activeInstitutionId";
export const ACTIVE_DATA_DATE_STORAGE_KEY = "activeDataDate";
const CHANGE_EVENT = "active-portfolio-change";

/**
 * Reads the in-session active portfolio override from sessionStorage. Returns `null`
 * when the user has not made an in-app selection, in which case callers should defer
 * to the server value (`user_getUserData.portfolioId`). sessionStorage scopes the
 * override to the browser tab/session so it is discarded when the session ends,
 * rather than persisting indefinitely as localStorage would.
 */
export function readActivePortfolioId(): number | null {
  const raw = sessionStorage.getItem(ACTIVE_PORTFOLIO_STORAGE_KEY);
  if (raw === null) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function readActiveInstitutionId(): number | null {
  const raw = sessionStorage.getItem(ACTIVE_INSTITUTION_STORAGE_KEY);
  if (raw === null) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function readActiveDataDate(): string | null {
  return sessionStorage.getItem(ACTIVE_DATA_DATE_STORAGE_KEY);
}

/**
 * Persists an active portfolio override and broadcasts the change to same-tab
 * subscribers. Used by the PA→PAX handoff (at boot) and the portfolios page. Pass the
 * current user's JWT `sub` as `ownerSub` so a later login by a different user in the
 * same tab can detect the override is stale and discard it.
 */
export function writeActivePortfolioId(
  portfolioId: number,
  institutionId?: number,
  ownerSub?: string,
  activeDataDate?: string
): void {
  sessionStorage.setItem(ACTIVE_PORTFOLIO_STORAGE_KEY, String(portfolioId));
  if (institutionId) {
    sessionStorage.setItem(ACTIVE_INSTITUTION_STORAGE_KEY, String(institutionId));
  }
  if (ownerSub) {
    sessionStorage.setItem(ACTIVE_PORTFOLIO_OWNER_KEY, ownerSub);
  }
  if (activeDataDate) {
    sessionStorage.setItem(ACTIVE_DATA_DATE_STORAGE_KEY, activeDataDate);
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

/**
 * Clears the active portfolio override and its owner stamp, then notifies same-tab
 * subscribers so they fall back to the server value.
 */
export function clearActivePortfolioId(): void {
  sessionStorage.removeItem(ACTIVE_PORTFOLIO_STORAGE_KEY);
  sessionStorage.removeItem(ACTIVE_INSTITUTION_STORAGE_KEY);
  sessionStorage.removeItem(ACTIVE_PORTFOLIO_OWNER_KEY);
  sessionStorage.removeItem(ACTIVE_DATA_DATE_STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

/**
 * Reconciles the override against the user who just authenticated. Called at boot once
 * the JWT principal is known, so a different user logging into an existing tab starts
 * from the server/DB portfolio instead of inheriting the previous user's pick.
 *
 * `didHandoff` is true when this boot carried a PA→PAX `?portfolioId=` handoff: that
 * value was seeded for the *incoming* user, so we claim ownership for `currentSub`
 * rather than treating the yet-unstamped override as stale. Otherwise, an override
 * whose owner is missing or differs from `currentSub` is discarded.
 */
export function reconcileActivePortfolioOwner(currentSub: string, didHandoff: boolean): void {
  if (readActivePortfolioId() === null) return;
  if (didHandoff) {
    sessionStorage.setItem(ACTIVE_PORTFOLIO_OWNER_KEY, currentSub);
    return;
  }
  if (sessionStorage.getItem(ACTIVE_PORTFOLIO_OWNER_KEY) !== currentSub) {
    clearActivePortfolioId();
  }
}

function subscribe(callback: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, callback);
  return () => {
    window.removeEventListener(CHANGE_EVENT, callback);
  };
}

/**
 * Reactive access to the in-session active portfolio override. Every consumer
 * re-renders when the value changes anywhere in the tab. The value is `null` until
 * the user selects a portfolio in-app (or a PA handoff seeds one at boot); resolve
 * the effective portfolio with `override ?? serverPortfolioId`.
 */
export function useActivePortfolio(): [
  {
    portfolioId: number | null;
    institutionId: number | null;
    activeDataDate: string | null;
  } | null,
  (portfolioId: number, institutionId: number, ownerSub?: string, activeDataDate?: string) => void,
] {
  const activePortfolioId = useSyncExternalStore(subscribe, readActivePortfolioId, () => null);
  const activeInstitutionId = useSyncExternalStore(subscribe, readActiveInstitutionId, () => null);
  const activeDataDate = useSyncExternalStore(subscribe, readActiveDataDate, () => null);

  const setActivePortfolioId = useCallback(
    (portfolioId: number, institutionId: number, ownerSub?: string, activeDataDate?: string) => {
      writeActivePortfolioId(portfolioId, institutionId, ownerSub, activeDataDate);
    },
    []
  );

  return [
    { portfolioId: activePortfolioId, institutionId: activeInstitutionId, activeDataDate },
    setActivePortfolioId,
  ];
}

/**
 * The portfolio the app should scope data to: the in-session override when present,
 * otherwise the server value seeded from PA (`user_getUserData.portfolioId`). Returns
 * `undefined` while the server value is still loading and no override exists.
 */
export function useEffectivePortfolioId(): {
  portfolioId: number | null;
  institutionId: number | null;
  activeDataDate: string | null;
} {
  const [override] = useActivePortfolio();
  const { data } = useUserDataQuery();
  return {
    portfolioId: override?.portfolioId ?? data?.user_getUserData?.portfolioData?.id ?? null,
    institutionId: override?.institutionId ?? data?.user_getUserData?.institutionId ?? null,
    activeDataDate:
      override?.activeDataDate ?? data?.user_getUserData?.portfolioData?.activeDataDate ?? null,
  };
}
